import React from 'react';
import { Alert, Avatar, Box, Button, Chip, Divider, Typography } from '@mui/material';
import { Modal } from '../../components/Common';
import { OrderStatus } from '../../types/orders.types';
import type { EnrichedReportedProblem } from '../../services';

const DASH = '—';

const formatDateTime = (iso?: string): string => {
  if (!iso) return DASH;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatEpoch = (epoch?: number): string => {
  if (!epoch) return DASH;
  return formatDateTime(new Date(epoch).toISOString());
};

const formatCurrency = (amount?: number): string =>
  amount === undefined ? DASH : `QAR ${amount.toFixed(2)}`;

/** Mirrors the order total arithmetic used by the Orders page list. */
const payableTotal = (order: NonNullable<EnrichedReportedProblem['order']>): number =>
  (order.total ?? 0) - (order.discountAmount ?? 0) + (order.shippingFee ?? 0);

const getOrderStatusColor = (status: OrderStatus): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case OrderStatus.APPROVED:
    case OrderStatus.COMPLETED:
      return 'success';
    case OrderStatus.CANCELLED:
    case OrderStatus.DECLINED:
      return 'error';
    case OrderStatus.REFUNDED:
    case OrderStatus.CHANGING:
      return 'warning';
    case OrderStatus.IN_PROGRESS:
    case OrderStatus.DELIVERY:
      return 'info';
    default:
      return 'default';
  }
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 'var(--palette-light-font-size-h4-value)',
      fontWeight: 700,
      color: 'var(--palette-light-text-primary-value-hex)',
    }}
  >
    {children}
  </Typography>
);

const PropertyRow = ({
  label,
  value,
  noBorder,
  mono,
}: {
  label: string;
  value: string | undefined;
  noBorder?: boolean;
  mono?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      gap: 'var(--palette-light-1-value)',
      padding: 'var(--palette-light-1-value) 0',
      borderBottom: noBorder ? 'none' : '1px solid #F1F4F4',
      alignItems: 'flex-start',
    }}
  >
    <Typography
      sx={{
        fontSize: 'var(--palette-light-font-size-default-value)',
        color: 'var(--palette-light-text-secondary-value-hex)',
        flex: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 'var(--palette-light-font-size-default-value)',
        color: 'var(--palette-light-text-primary-value-hex)',
        flex: 1,
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        ...(mono && { fontFamily: 'monospace', fontSize: 12 }),
      }}
    >
      {value || 'N/A'}
    </Typography>
  </Box>
);

const SectionBox = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
    {children}
  </Box>
);

interface PropertyRowSpec {
  label: string;
  value: string | undefined;
  mono?: boolean;
}

const PropertyRows = ({ rows }: { rows: PropertyRowSpec[] }) => (
  <>
    {rows.map((row, index) => (
      <PropertyRow
        key={row.label}
        label={row.label}
        value={row.value}
        mono={row.mono}
        noBorder={index === rows.length - 1}
      />
    ))}
  </>
);

interface ProblemDetailsModalProps {
  problem: EnrichedReportedProblem | null;
  onClose: () => void;
  onResolve: (problem: EnrichedReportedProblem) => void;
}

/**
 * Full triage view for a single reported problem: the report itself, the
 * reporting user, the linked order and that order's vendor. Every section is
 * omitted when the data is absent, which is the case for reports created
 * before order linking existed.
 */
const ProblemDetailsModal: React.FC<ProblemDetailsModalProps> = ({ problem, onClose, onResolve }) => (
  <Modal open={problem !== null} onClose={onClose} title="Reported Problem" width={720}>
    {problem && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionBox>
          <SectionTitle>Report</SectionTitle>
          <PropertyRows
            rows={[
              { label: 'Description', value: problem.description },
              { label: 'Status', value: problem.status === 'resolved' ? 'Resolved' : 'Open' },
              { label: 'Reported', value: formatDateTime(problem.createdAt) },
              { label: 'Problem ID', value: problem.problemId, mono: true },
              ...(problem.status === 'resolved'
                ? [
                  { label: 'Resolved', value: formatDateTime(problem.resolvedAt) },
                  { label: 'Resolved by', value: problem.resolvedBy, mono: true },
                ]
                : []),
            ]}
          />
        </SectionBox>

        <Divider />

        <SectionBox>
          <SectionTitle>Reported by</SectionTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 'var(--palette-light-1-value) 0' }}>
            <Avatar src={problem.user.imageProfile || undefined} alt={problem.user.name} />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 600,
                  color: 'var(--palette-light-text-primary-value-hex)',
                }}
              >
                {problem.user.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-small-value)',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                }}
              >
                {problem.user.phone || 'No phone on file'}
              </Typography>
            </Box>
          </Box>
              <PropertyRows
                rows={[
                  { label: 'User ID', value: problem.user.userId, mono: true },
                  { label: 'Phone', value: problem.user.phone },
                  { label: 'Email', value: problem.user.email },
                ]}
              />
            </SectionBox>

        {problem.order && (
          <>
            <Divider />
            <SectionBox>
              <SectionTitle>Order</SectionTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: 'var(--palette-light-1-value) 0' }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  {problem.order.orderNumber}
                </Typography>
                <Chip
                  label={problem.order.status}
                  size="small"
                  variant="outlined"
                  color={getOrderStatusColor(problem.order.status)}
                />
                {problem.order.hasDispute && (
                  <Chip label="Disputed" size="small" variant="outlined" color="error" />
                )}
                {problem.order.hasRefund && (
                  <Chip label="Refunded" size="small" variant="outlined" color="warning" />
                )}
              </Box>
              <PropertyRows
                rows={[
                  { label: 'Order ID', value: problem.order.orderId, mono: true },
                  { label: 'Product', value: problem.order.productName },
                  ...(problem.order.productDetails
                    ? [{ label: 'Packages', value: problem.order.productDetails }]
                    : []),
                  { label: 'Order placed', value: formatEpoch(problem.order.createdAt) },
                  { label: 'Event date', value: formatDateTime(problem.order.eventDateTime) },
                  {
                    label: 'Event address',
                    value: problem.order.eventAddress
                      ? [problem.order.eventAddress, problem.order.eventZone]
                        .filter(Boolean)
                        .join(', ')
                      : undefined,
                  },
                  { label: 'Amount', value: formatCurrency(problem.order.total) },
                  ...(problem.order.discountAmount !== undefined
                    ? [
                      {
                        label: 'Discount',
                        value: [formatCurrency(problem.order.discountAmount), problem.order.discountType]
                          .filter(Boolean)
                          .join(' '),
                      },
                    ]
                    : []),
                  ...(problem.order.shippingFee !== undefined
                    ? [{ label: 'Shipping', value: formatCurrency(problem.order.shippingFee) }]
                    : []),
                  { label: 'Payable total', value: formatCurrency(payableTotal(problem.order)) },
                  ...(problem.order.feeAmount !== undefined
                    ? [{ label: 'Platform fee', value: formatCurrency(problem.order.feeAmount) }]
                    : []),
                  ...(problem.order.vendorPayoutAmount !== undefined
                    ? [{ label: 'Vendor payout', value: formatCurrency(problem.order.vendorPayoutAmount) }]
                    : []),
                  { label: 'Contact name', value: problem.order.contactName },
                  { label: 'Contact phone', value: problem.order.contactPhone },
                  ...(problem.order.specialRequests
                    ? [{ label: 'Special requests', value: problem.order.specialRequests }]
                    : []),
                  ...(problem.order.disputeReason
                    ? [{ label: 'Dispute reason', value: problem.order.disputeReason }]
                    : []),
                ]}
              />
            </SectionBox>
          </>
        )}

        {problem.vendor && (
          <>
            <Divider />
            <SectionBox>
              <SectionTitle>Vendor</SectionTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 'var(--palette-light-1-value) 0' }}>
                <Avatar src={problem.vendor.profileImageUrl || undefined} alt={problem.vendor.companyName} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 'var(--palette-light-font-size-default-value)',
                      fontWeight: 600,
                      color: 'var(--palette-light-text-primary-value-hex)',
                    }}
                  >
                    {problem.vendor.companyName}
                  </Typography>
                  {problem.vendor.status && (
                    <Chip label={problem.vendor.status} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
              <PropertyRows
                rows={[
                  { label: 'Vendor ID', value: problem.vendor.vendorId, mono: true },
                  { label: 'Contact person', value: problem.vendor.contactName },
                  { label: 'Phone', value: problem.vendor.phone },
                  { label: 'Email', value: problem.vendor.email },
                ]}
              />
            </SectionBox>
          </>
        )}

        {!problem.order && (
          <Alert severity="info">
            This report is not linked to an order, so no order or vendor details are available.
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          {problem.status !== 'resolved' && (
            <Button variant="contained" onClick={() => onResolve(problem)}>
              Resolve
            </Button>
          )}
        </Box>
      </Box>
    )}
  </Modal>
);

export default ProblemDetailsModal;
